import { configurePlatform, configureVanceWs } from '@vance/shared';
import { refreshAccessCookie } from './refreshWeb';
import { getSessionData, hydrateIdentity } from './webUiSession';
import { loadMe, onMeChanged } from './me';
import { applyTheme, ensurePrintLightTheme } from './themeWeb';
import { applyTenantCustomCss } from './tenantCustomCss';
import { storageWeb } from './storageWeb';
import { installSwipeHistory } from './swipeHistory';
import {
  onDocumentChanged,
  onDocumentChangedPrefix,
  onDocumentPrefixReconnect,
  onPointer,
  onPointerLeave,
  sendPointerMove,
  subscribeDocument,
  subscribeDocumentPrefix,
  subscribePointers,
  unsubscribeDocument,
  unsubscribeDocumentPrefix,
  unsubscribePointers,
} from '@/ws/wsConnectionStore';

/**
 * Web boot hook. Imported for side effect at the top of every
 * editor's `main.ts` so `configurePlatform` runs before any other
 * `@vance/shared` module touches storage or the network.
 *
 * The base URL is always the page's own origin — in production the
 * SPA is same-origin-served by the brain, in `vite dev` the
 * dev-server proxies `/brain/*` to `http://localhost:9990`. No
 * build-time environment variable: deployment-specific values like
 * the brain URL belong in the runtime `config.json` written by the
 * pod entrypoint, not baked into the JS bundle.
 *
 * After binding, mirror the cookie-derived identity into the
 * prefsStore so `getTenantId()` / `getUsername()` from
 * `@vance/shared` find the values via the same path Mobile uses.
 */
function resolveBaseUrl(): string {
  return `${window.location.protocol}//${window.location.host}`;
}

function redirectToLogin(): void {
  const next = encodeURIComponent(
    window.location.pathname + window.location.search + window.location.hash,
  );
  window.location.href = `/login.html?next=${next}`;
}

configurePlatform({
  storage: storageWeb,
  rest: {
    baseUrl: resolveBaseUrl(),
    authMode: 'cookie',
    refreshAccess: refreshAccessCookie,
    onUnauthorized: redirectToLogin,
  },
});

// Expose the wsConnectionStore singleton through `@vance/shared` so
// Module-Federation addons (Calendar, Kanban, Slideshow, …) can drive
// the same WebSocket / subscription set. See
// `repos/vance/client/packages/shared/src/ws/bridge.ts` for the
// rationale (each addon ships its own `@vance/shared` copy; the
// `__VANCE_WS__` globalThis stash is the rendezvous point).
configureVanceWs({
  subscribeDocument,
  unsubscribeDocument,
  onDocumentChanged,
  subscribeDocumentPrefix,
  unsubscribeDocumentPrefix,
  onDocumentChangedPrefix,
  onDocumentPrefixReconnect,
  subscribePointers,
  unsubscribePointers,
  sendPointerMove,
  onPointer,
  onPointerLeave,
});

// Cookie-derived identity → prefsStore. Idempotent — runs every page
// load, ensuring the values stay synchronised with the data cookie.
hydrateIdentity();
// Paint the resolved theme as early as possible — before Vue mounts,
// so the first frame matches the user's choice. "auto" attaches a
// matchMedia listener that keeps tracking the OS preference live.
applyTheme();
// Printing pins the light theme for the duration of the job — a dark
// page reaches the printer as white-on-white.
ensurePrintLightTheme();
// The theme above came from the last `/me` snapshot; the fresh answer may
// carry a different one (changed in another tab or on another device).
onMeChanged((next, previous) => {
  if (next?.settings?.['webui.theme'] !== previous?.settings?.['webui.theme']) applyTheme();
});
// The signed-in user — identity, roles, settings, status — fresh for this
// page. Fire-and-forget: the snapshot carries the first frame.
if (getSessionData()) void loadMe();
// Tenant custom stylesheet — fetched after hydrateIdentity so the
// tenant is known, fire-and-forget so boot never waits on it. Injected
// after applyTheme so the tenant sheet is the last one in the head and
// wins the cascade against the theme of equal specificity.
void applyTenantCustomCss();
// Two-finger trackpad swipe = back/forward, desktop app only (the
// module gates on the bridge; browsers keep their native swipe).
// Every editor page boots through here, so the swipe works everywhere.
installSwipeHistory();
